import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Botao,
  Breadcrumb,
  Cabecalho,
  Cores,
  CpfInputUnform,
  DataInputUnform,
  ErroLayoutContainer,
  Filtro,
  IconeDeListaDeDocumentos,
  IconeRevelarSenha,
  InputUnform,
  SelectUnform,
  Tooltip
} from 'src/componentes'
import { FuncoesDataHora } from 'src/componentes/funcoes'
import {
  Tabela,
  TabelaDados,
  TabelaRef,
  TabelaResposta
} from 'src/componentes/tabela'
import { RotasRescisoes } from 'src/rotas'
import { Api } from 'src/servicos'
import {
  CorStatusRescisao,
  SetorRescisao,
  SetorRescisaoPorExtenso,
  StatusRescisao,
  StatusRescisaoPorExtenso,
  TipoOrdenacao
} from 'src/tipos'

import {
  ModalNovoPedidoRescisao,
  ModalNovoPedidoRescisaoRef
} from './componentes/modal-novo-pedido'
import {
  BadgeSemStatus,
  BadgeStatus,
  Campo,
  Container,
  ContainerAcoes,
  Dados,
  Form,
  IconeAcao
} from './styles'

interface DadosPesquisaRescisao {
  nomeAluno?: string
  cpf?: string
  setor?: string
  status?: string[]
  numeroProtocolo?: string
  dataAberturaInicio?: string
  dataAberturaFim?: string
}

const renderizarBadgeStatus = (status: string) => {
  const chave = status as keyof typeof StatusRescisao
  const cor = CorStatusRescisao[chave]
  const texto = StatusRescisaoPorExtenso[chave] ?? status

  if (!cor) {
    return <BadgeSemStatus>{texto}</BadgeSemStatus>
  }

  return (
    <BadgeStatus fundo={cor.fundo} corTexto={cor.texto}>
      {texto}
    </BadgeStatus>
  )
}

export const PaginaRescisoes: React.FC = () => {
  const tabelaRef = useRef<TabelaRef>()
  const modalNovoPedido = useRef<ModalNovoPedidoRescisaoRef>(null)
  const [dadosPesquisa, definirDadosPesquisa] = useState<DadosPesquisaRescisao>(
    null
  )
  const [paginas, definirPaginas] = useState(null)
  const history = useHistory()

  const acaoPesquisa = (dados: DadosPesquisaRescisao) => {
    if (dados !== null && paginas !== 1) {
      definirPaginas(1)
    }

    definirDadosPesquisa(dados)
  }

  const obterDados = async (
    numeroPaginas: number,
    quantidadePorPagina: number,
    chaveOrdenacao?: string,
    ordem?: TipoOrdenacao
  ): Promise<TabelaResposta | null> => {
    if (paginas !== null) {
      numeroPaginas = paginas
    }

    const dados = await Api.ListarProtocolosRescisao(
      {
        nomeAluno: dadosPesquisa?.nomeAluno,
        cpf: dadosPesquisa?.cpf,
        setor: dadosPesquisa?.setor,
        status: dadosPesquisa?.status,
        numeroProtocolo: dadosPesquisa?.numeroProtocolo,
        dataAberturaInicio: dadosPesquisa?.dataAberturaInicio,
        dataAberturaFim: dadosPesquisa?.dataAberturaFim
      },
      numeroPaginas,
      quantidadePorPagina,
      chaveOrdenacao,
      ordem
    )

    if (!dados) return null

    definirPaginas(null)

    return {
      Dados: dados.itens,
      Paginacao: {
        Pagina: numeroPaginas,
        TotalRegistros: dados.quantidade
      }
    } as TabelaResposta
  }

  const recarregarTabela = () => tabelaRef.current?.CarregarDados()

  const abrirDetalhe = (id: string) =>
    history.push(`${RotasRescisoes.Detalhe}/${id}`)

  const abrirHistorico = (id: string) =>
    history.push(`${RotasRescisoes.Historico}/${id}`)

  useEffect(() => {
    if (dadosPesquisa === null) return
    tabelaRef.current.CarregarDados()
  }, [dadosPesquisa])

  return (
    <>
      <Cabecalho>
        <Breadcrumb
          titulo="Rescisões"
          atalhos={[{ texto: 'Rescisões' }]}
        />
        <Botao
          type="button"
          id="btn-incluir-rescisao"
          texto="Incluir"
          tema="Secundario"
          onClick={() => modalNovoPedido.current?.abrir()}
        />
      </Cabecalho>
      <Filtro>
        <Form acaoSucesso={acaoPesquisa}>
          <Container>
            <Campo mr>
              <InputUnform
                type="text"
                name="nomeAluno"
                label="Nome do Aluno"
                maxLength={200}
              />
            </Campo>
            <Campo mr>
              <CpfInputUnform type="text" name="cpf" label="CPF" />
            </Campo>
            <Campo mr>
              <InputUnform
                type="text"
                name="numeroProtocolo"
                label="Nº Protocolo"
                maxLength={50}
              />
            </Campo>
            <Campo>
              <SelectUnform
                id="slct_setor"
                name="setor"
                label="Setor"
                iconeFechar
                opcoes={[
                  {
                    id: SetorRescisao.Academico,
                    texto: SetorRescisaoPorExtenso.Academico
                  },
                  {
                    id: SetorRescisao.Financeiro,
                    texto: SetorRescisaoPorExtenso.Financeiro
                  }
                ]}
              />
            </Campo>
          </Container>
          <Container>
            <Campo mr>
              <SelectUnform
                id="slct_status"
                name="status"
                label="Status"
                iconeFechar
                multiplo
                opcoes={Object.keys(StatusRescisao).map(chave => ({
                  id: chave,
                  texto:
                    StatusRescisaoPorExtenso[
                      chave as keyof typeof StatusRescisao
                    ]
                }))}
              />
            </Campo>
            <Campo mr>
              <DataInputUnform
                name="dataAberturaInicio"
                label="Data Abertura Início"
              />
            </Campo>
            <Campo>
              <DataInputUnform
                name="dataAberturaFim"
                label="Data Abertura Fim"
              />
            </Campo>
          </Container>
          <Botao
            type="submit"
            id="btn-aplicar-filtro"
            texto="Aplicar filtro"
            tema="Link"
          />
        </Form>
      </Filtro>
      <Dados>
        <Tabela
          ref={tabelaRef}
          Colunas={[
            {
              Chave: 'numeroProtocolo',
              RenderizarCabecalho: () => (
                <div style={{ width: 90 }}>Protocolo</div>
              )
            },
            {
              Chave: 'nomeAluno',
              RenderizarCabecalho: () => <div style={{ width: 110 }}>Nome</div>
            },
            {
              Chave: 'dataAbertura',
              Renderizar: (linha: TabelaDados) => (
                <span>
                  {FuncoesDataHora.dataStringFormatadaComBarras(
                    linha.dataAbertura
                  )}
                </span>
              ),
              RenderizarCabecalho: () => (
                <div style={{ width: 80 }}>Data Abertura</div>
              )
            },
            {
              Chave: 'dataFimProtocolo',
              Renderizar: (linha: TabelaDados) => (
                <span>
                  {linha.dataFimProtocolo
                    ? FuncoesDataHora.dataStringFormatadaComBarras(
                        linha.dataFimProtocolo
                      )
                    : '—'}
                </span>
              ),
              RenderizarCabecalho: () => (
                <div style={{ width: 70 }}>Data Fim</div>
              )
            },
            {
              Chave: 'status',
              Renderizar: (linha: TabelaDados) =>
                renderizarBadgeStatus(linha.status),
              RenderizarCabecalho: () => (
                <div style={{ width: 100 }}>Status</div>
              )
            },
            {
              Chave: 'setorResponsavel',
              Renderizar: (linha: TabelaDados) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  {linha.setorResponsavel
                    ? SetorRescisaoPorExtenso[
                        linha.setorResponsavel as keyof typeof SetorRescisao
                      ] ?? linha.setorResponsavel
                    : '—'}
                </span>
              ),
              RenderizarCabecalho: () => <div style={{ width: 90 }}>Setor</div>
            },
            {
              Chave: 'motivo',
              Renderizar: (linha: TabelaDados) => <>{linha.motivo ?? '—'}</>,
              RenderizarCabecalho: () => <div style={{ width: 90 }}>Motivo</div>
            },
            {
              Chave: 'modalidade',
              Renderizar: (linha: TabelaDados) => (
                <>{linha.modalidade ?? '—'}</>
              ),
              RenderizarCabecalho: () => (
                <div style={{ width: 80 }}>Modalidade</div>
              )
            },
            {
              Chave: 'curso',
              Renderizar: (linha: TabelaDados) => <>{linha.curso ?? '—'}</>,
              RenderizarCabecalho: () => (
                <div style={{ width: 100 }}>Turma/Curso</div>
              )
            },
            {
              Chave: 'acoes',
              RenderizarCabecalho: () => <div style={{ width: 70 }}>Ações</div>,
              Renderizar: (linha: TabelaDados) => {
                const DETALHE_ID = `RESCISAO_DETALHE/${linha.id}`
                const HISTORICO_ID = `RESCISAO_HISTORICO/${linha.id}`

                return (
                  <ContainerAcoes>
                    <IconeAcao
                      onClick={() => abrirDetalhe(linha.id)}
                      onContextMenu={() => {
                        const rota = window.open(
                          `${RotasRescisoes.Detalhe}/${linha.id}`
                        )
                        rota?.focus()
                      }}
                      data-tip="Abrir detalhe do protocolo"
                      data-for={DETALHE_ID}
                    >
                      {IconeRevelarSenha}
                      <Tooltip
                        id={DETALHE_ID}
                        textColor={Cores.PRETO}
                        backgroundColor={Cores.CINZA_1_CLARO}
                        place="right"
                      />
                    </IconeAcao>
                    <IconeAcao
                      onClick={() => abrirHistorico(linha.id)}
                      data-tip="Ver histórico do protocolo"
                      data-for={HISTORICO_ID}
                    >
                      {IconeDeListaDeDocumentos}
                      <Tooltip
                        id={HISTORICO_ID}
                        textColor={Cores.PRETO}
                        backgroundColor={Cores.CINZA_1_CLARO}
                        place="right"
                      />
                    </IconeAcao>
                  </ContainerAcoes>
                )
              }
            }
          ]}
          ObterDados={obterDados}
          ComPaginacao
        />
      </Dados>
      <ModalNovoPedidoRescisao
        ref={modalNovoPedido}
        backdrop
        aoCriar={(id: string) => {
          toast('Pedido de rescisão criado com sucesso!', { type: 'success' })
          recarregarTabela()
          abrirDetalhe(id)
        }}
      />
    </>
  )
}

export default (): JSX.Element => (
  <ErroLayoutContainer.Provider>
    <PaginaRescisoes />
  </ErroLayoutContainer.Provider>
)

export { default as PaginaDetalheRescisao } from './detalhe'
export { default as PaginaHistoricoRescisao } from './historico'
